import { format, subDays } from 'date-fns';
import { syncChamadosFromAPI } from '@/api/chamados-prospera';
import { STATUS_CHAMADOS, TIPOS_CHAMADOS } from '@/types/chamados';

/** Status em aberto (todos exceto finalizado e cancelado). */
export const STATUS_CHAMADOS_ABERTOS = STATUS_CHAMADOS.filter(
  (s) => s !== 'finalizado' && s !== 'cancelado'
);

export const STATUS_CHAMADOS_ENCERRADOS = ['finalizado', 'cancelado'] as const;

export type SyncChamadosWithApiOptions = {
  /** Janela em dias para finalizados/cancelados (etapa 2). Padrão: 30. */
  closedDaysRange?: number;
  limite?: number;
};

export type SyncChamadosPassoResult = {
  total: number;
  salvos: number;
};

export type SyncChamadosWithApiResult = {
  passo1: SyncChamadosPassoResult;
  passo2: SyncChamadosPassoResult;
  totalApi: number;
  totalSalvos: number;
};

/**
 * Importação em duas etapas (espelha a estratégia recomendada para chamados na API Prospera):
 * 1) Todos os status exceto finalizado/cancelado, sem filtro de data, todos os tipos.
 * 2) Somente finalizado e cancelado, últimos N dias (data última alteração de status), todos os tipos.
 */
export async function syncChamadosWithApi(
  options?: SyncChamadosWithApiOptions
): Promise<SyncChamadosWithApiResult> {
  const closedDaysRange =
    typeof options?.closedDaysRange === 'number' && options.closedDaysRange > 0 && options.closedDaysRange <= 365
      ? options.closedDaysRange
      : 30;
  const limite = options?.limite ?? 5000;
  const todosTipos = [...TIPOS_CHAMADOS];

  const passo1Result = await syncChamadosFromAPI({
    filtros: {
      status: [...STATUS_CHAMADOS_ABERTOS],
      tipo: todosTipos,
    },
    limite,
  });

  const end = new Date();
  const start = subDays(end, closedDaysRange);
  const passo2Result = await syncChamadosFromAPI({
    filtros: {
      status: [...STATUS_CHAMADOS_ENCERRADOS],
      tipo: todosTipos,
      dataUltimaAlteracaoInicio: format(start, 'yyyy-MM-dd'),
      dataUltimaAlteracaoFim: format(end, 'yyyy-MM-dd'),
    },
    limite,
  });

  const passo1 = { total: passo1Result.total, salvos: passo1Result.salvos };
  const passo2 = { total: passo2Result.total, salvos: passo2Result.salvos };

  return {
    passo1,
    passo2,
    totalApi: passo1.total + passo2.total,
    totalSalvos: passo1.salvos + passo2.salvos,
  };
}
